import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { motion, useReducedMotion } from "motion/react";
import type { Funnel } from "../../shared/types.ts";
import { cn } from "./cn";
import { ease } from "./motion";

type Key = "swiped" | "sent" | "waiting" | "failed" | "interviews" | "noReply" | "rejected" | "offers" | "interviewing" | "noOffer";

/** Each stage of the chart, column by column, top to bottom: the good outcome, the open one, then the closed one. `color` is a token, painted at `alpha` over white. */
const NODES: { key: Key; label: string; short?: string; col: number; parent?: Key; color: string; alpha: number }[] = [
  { key: "swiped", label: "Swiped right", short: "Swiped", col: 0, color: "--primary", alpha: 1 },
  { key: "sent", label: "Sent", col: 1, parent: "swiped", color: "--primary", alpha: 1 },
  { key: "waiting", label: "Sending", col: 1, parent: "swiped", color: "--primary", alpha: 0.3 },
  { key: "failed", label: "Didn't send", col: 1, parent: "swiped", color: "--cream-ink", alpha: 0.4 },
  { key: "interviews", label: "Interviews", col: 2, parent: "sent", color: "--accent", alpha: 1 },
  { key: "noReply", label: "No reply", col: 2, parent: "sent", color: "--muted", alpha: 0.38 },
  { key: "rejected", label: "Rejected", col: 2, parent: "sent", color: "--tone-red-ink", alpha: 0.5 },
  { key: "offers", label: "Offers", col: 3, parent: "interviews", color: "--tone-accent-ink", alpha: 1 },
  { key: "interviewing", label: "Interviewing", col: 3, parent: "interviews", color: "--accent", alpha: 0.4 },
  { key: "noOffer", label: "No offer", col: 3, parent: "interviews", color: "--tone-red-ink", alpha: 0.38 },
];

export type SankeyNode = { key: Key; label: string; value: number; col: number; x: number; y: number; h: number; color: string; alpha: number; labelX: number; labelY: number; above: boolean };
export type SankeyLink = { from: SankeyNode; to: SankeyNode; d: string; x0: number; x1: number };
export type SankeyLayout = { width: number; height: number; nodeWidth: number; fontSize: number; nodes: SankeyNode[]; links: SankeyLink[] };

export type SankeyOptions = {
  width: number;
  height: number;
  fontSize: number;
  nodeWidth?: number;
  gap?: number;
  /** Width of a label in px. Used to leave room on the right for the last column. */
  measure?: (label: string, count: string, fontSize: number) => number;
};

export const sankeyFont = (weight: number, size: number) => `${weight} ${size}px "Plus Jakarta Sans", ui-sans-serif, system-ui, sans-serif`;

let ruler: CanvasRenderingContext2D | null | undefined;

/** A label's width as the chart sets it: the name at 600, a space, and the count at 800. Estimated where there's no canvas. */
export function measureLabel(label: string, count: string, size: number): number {
  if (ruler === undefined) ruler = typeof document === "undefined" ? null : document.createElement("canvas").getContext("2d");
  if (!ruler) return (label.length + count.length + 1) * size * 0.6;
  ruler.font = sankeyFont(600, size);
  const a = ruler.measureText(`${label} `).width;
  ruler.font = sankeyFont(800, size);
  return a + ruler.measureText(count).width;
}

/** The counts each stage shows. Interviewing is what's left of interviews once offers and no offers are out. */
export function sankeyValues(f: Funnel): Record<Key, number> {
  const n = (v: number) => Math.max(0, Math.round(v || 0));
  return {
    swiped: n(f.swiped),
    sent: n(f.sent),
    waiting: n(f.waiting),
    failed: n(f.failed),
    interviews: n(f.interviews),
    noReply: n(f.noReply),
    rejected: n(f.rejected),
    offers: n(f.offers),
    interviewing: n(f.interviews - f.offers - f.noOffer),
    noOffer: n(f.noOffer),
  };
}

/** Pushes label centres apart so no two overlap, keeping them inside `[lo, hi]`. */
function spread(ys: number[], space: number, lo: number, hi: number): number[] {
  const out = ys.map((y) => Math.min(hi, Math.max(lo, y)));
  for (let i = 1; i < out.length; i++) out[i] = Math.max(out[i]!, out[i - 1]! + space);
  if (out.length && out[out.length - 1]! > hi) out[out.length - 1] = hi;
  for (let i = out.length - 2; i >= 0; i--) out[i] = Math.min(out[i]!, out[i + 1]! - space);
  return out;
}

/** Splits `total` px between the column gaps as evenly as it can while giving gap `i` at least `need[i]`. */
function share(need: number[], total: number): number[] {
  const sum = need.reduce((s, n) => s + n, 0);
  if (sum >= total) return need.map((n) => (sum ? (n * total) / sum : total / need.length));
  const fixed = new Set<number>();
  for (;;) {
    const free = need.map((_, i) => i).filter((i) => !fixed.has(i));
    const each = (total - [...fixed].reduce((s, i) => s + need[i]!, 0)) / free.length;
    const tight = free.filter((i) => need[i]! > each);
    if (!tight.length) return need.map((n, i) => (fixed.has(i) ? n : each));
    for (const i of tight) fixed.add(i);
  }
}

/** Merges ranges a label centre can't sit in. */
function merge(ranges: [number, number][]): [number, number][] {
  const out: [number, number][] = [];
  for (const [a, b] of [...ranges].sort((p, q) => p[0] - q[0])) {
    const m = out[out.length - 1];
    if (m && a <= m[1]) m[1] = Math.max(m[1], b);
    else out.push([a, b]);
  }
  return out;
}

const clear = (y: number, blocked: [number, number][]) => blocked.every(([a, b]) => y <= a + 0.01 || y >= b - 0.01);

/** The centre nearest `want`, no higher than `floor`, outside every blocked range. */
function nearestClear(want: number, floor: number, blocked: [number, number][]): number {
  const y = Math.max(want, floor);
  const hit = blocked.find(([a, b]) => y > a + 0.01 && y < b - 0.01);
  if (!hit) return y;
  return hit[0] >= floor && y - hit[0] <= hit[1] - y ? hit[0] : hit[1];
}

/**
 * Lays the funnel out in px: nodes as bars in up to four columns, links as cubic ribbons from each parent to its children.
 * Stages that split further are labelled in a lane above their bar, so no label sits on a ribbon; the rest beside it. Zero stages drop out. Bars have a minimum height so a single offer still shows. Pure, so the share image draws the same chart.
 * Columns widen where a lane label needs the room. A side label that would run into a later bar or lane label moves its bar
 * down until it clears, and the bars shrink to make room for that and for labels pushed past the bottom.
 */
export function layoutSankey(funnel: Funnel, opts: SankeyOptions): SankeyLayout {
  let reserve = 0;
  const extra = new Map<Key, number>();
  for (let i = 0; ; i++) {
    const final = i === 4;
    const { layout, overflow, shifts } = build(funnel, opts, reserve, extra, final);
    if (final) return layout;
    if (shifts.size) for (const [k, d] of shifts) extra.set(k, (extra.get(k) ?? 0) + d);
    else if (overflow > 0.5) reserve += overflow;
    else return layout;
  }
}

/** One pass of the layout. `extra` is space added above a bar so its label clears; `shifts` is more of it this pass found it needs. */
function build(
  funnel: Funnel,
  opts: SankeyOptions,
  reserve: number,
  extra: Map<Key, number>,
  final: boolean,
): { layout: SankeyLayout; overflow: number; shifts: Map<Key, number> } {
  const { width, height, fontSize } = opts;
  const nodeWidth = opts.nodeWidth ?? 8;
  const gap = opts.gap ?? 10;
  const measure = opts.measure ?? measureLabel;
  const values = sankeyValues(funnel);
  const pad = Math.ceil(fontSize * 0.7);
  const lane = Math.round(fontSize * 1.9);
  const top = lane;
  const inner = height - lane - pad - reserve;
  const minH = 3;

  const shown = NODES.filter((n) => values[n.key] > 0 && (!n.parent || values[n.parent] > 0));
  const kids = (k: Key) => shown.filter((n) => n.parent === k);
  const cols = [...new Set(shown.map((n) => n.col))].sort();
  const empty: SankeyLayout = { width, height, nodeWidth, fontSize, nodes: [], links: [] };
  const shifts = new Map<Key, number>();
  if (!values.swiped || !shown.length) return { layout: empty, overflow: 0, shifts };

  const firstKids = kids("swiped");
  const pushed = Math.max(0, ...cols.map((c) => shown.filter((n) => n.col === c).reduce((s, n) => s + (extra.get(n.key) ?? 0), 0)));
  const room = inner - gap * Math.max(0, firstKids.length - 1) - pushed;
  const heights = new Map<Key, number>();
  const size = (k: Key, scale: number): number => {
    const c = kids(k);
    const h = c.length ? c.reduce((s, n) => s + size(n.key, scale), 0) : Math.max(minH, values[k] * scale);
    heights.set(k, h);
    return h;
  };
  let scale = room / values.swiped;
  for (let i = 0; i < 4; i++) {
    const total = size("swiped", scale);
    if (total <= room + 0.5) break;
    scale *= room / total;
  }
  size("swiped", scale);

  const last = cols[cols.length - 1]!;
  const lastLabels = shown.filter((n) => n.col === last).map((n) => measure(n.label, String(values[n.key]), fontSize));
  const right = width - nodeWidth - Math.ceil(Math.max(0, ...lastLabels)) - Math.round(fontSize * 0.6) - 2;

  const lead = cols.map((c) => shown.find((n) => n.col === c && kids(n.key).length > 0));
  const even = cols.length > 1 ? right / (cols.length - 1) : right;
  const labels = new Map<Key, string>(shown.map((n) => [n.key, n.label]));
  lead.forEach((n, i) => {
    if (n?.short && lead[i + 1] && measure(n.label, String(values[n.key]), fontSize) > even - fontSize) labels.set(n.key, n.short);
  });
  const need = cols.slice(1).map((_, i) => {
    const n = lead[i];
    return n && lead[i + 1] ? measure(labels.get(n.key)!, String(values[n.key]), fontSize) + fontSize : 0;
  });
  const gaps = need.length ? share(need, right) : [];
  const colX = (c: number) => gaps.slice(0, cols.indexOf(c)).reduce((s, g) => s + g, 0);

  const nodes = new Map<Key, SankeyNode>();
  const place = (n: (typeof NODES)[number], y: number) =>
    nodes.set(n.key, { key: n.key, label: labels.get(n.key)!, value: values[n.key], col: n.col, x: colX(n.col), y, h: heights.get(n.key)!, color: n.color, alpha: n.alpha, labelX: 0, labelY: 0, above: false });

  const stack = (group: typeof shown, centre: number) => {
    const span = group.reduce((s, n) => s + heights.get(n.key)! + (extra.get(n.key) ?? 0), 0) + gap * (group.length - 1);
    let y = Math.min(top + inner - span, Math.max(top, centre - span / 2));
    for (const n of group) {
      y += extra.get(n.key) ?? 0;
      place(n, y);
      y += heights.get(n.key)! + gap;
    }
  };

  place(NODES[0]!, top);
  stack(firstKids, top);
  for (const parent of ["sent", "interviews"] as const) {
    const p = nodes.get(parent);
    if (p) stack(kids(parent), p.y + p.h / 2);
  }

  const links: SankeyLink[] = [];
  for (const p of nodes.values()) {
    let sy = p.y;
    for (const c of kids(p.key)) {
      const t = nodes.get(c.key)!;
      const x0 = p.x + nodeWidth;
      const x1 = t.x;
      const mx = (x0 + x1) / 2;
      const s1 = sy + t.h;
      const d = `M${x0},${sy}C${mx},${sy} ${mx},${t.y} ${x1},${t.y}L${x1},${t.y + t.h}C${mx},${t.y + t.h} ${mx},${s1} ${x0},${s1}Z`;
      links.push({ from: p, to: t, d, x0, x1 });
      sy = s1;
    }
  }

  const list = [...nodes.values()];
  const wide = (n: SankeyNode) => measure(n.label, String(n.value), fontSize);
  for (const n of list) {
    n.above = kids(n.key).length > 0;
    n.labelX = n.above ? n.x : n.x + nodeWidth + Math.round(fontSize * 0.5);
    if (n.above) n.labelY = n.y - Math.round(fontSize * 0.95);
  }

  const space = fontSize + 5;
  const slack = Math.round(fontSize * 0.3);
  const lo = pad;
  const hi = height - pad;
  const obstacles = list.flatMap((n) => [
    { key: n.key, x0: n.x, x1: n.x + nodeWidth, y0: n.y, y1: n.y + n.h },
    ...(n.above ? [{ key: n.key, x0: n.labelX, x1: n.labelX + wide(n), y0: n.labelY - fontSize / 2, y1: n.labelY + fontSize / 2 }] : []),
  ]);
  let overflow = 0;
  for (const c of cols) {
    const col = list.filter((n) => n.col === c && !n.above).sort((a, b) => a.y - b.y);
    const blocked = col.map((n) => {
      const x0 = n.labelX - slack;
      const x1 = n.labelX + wide(n) + slack;
      return merge(
        obstacles.filter((o) => o.key !== n.key && o.x1 > x0 && o.x0 < x1).map((o): [number, number] => [o.y0 - slack - fontSize / 2, o.y1 + slack + fontSize / 2]),
      );
    });
    let prev = -Infinity;
    let ys = col.map((n, i) => {
      const floor = Math.max(lo, prev + space);
      const want = Math.max(n.y + n.h / 2, floor);
      prev = nearestClear(want, floor, blocked[i]!);
      if (prev > want && !final && !shifts.size) shifts.set(n.key, prev - want);
      return prev;
    });
    if (ys.length && ys[ys.length - 1]! > hi) {
      const up = [...ys];
      up[up.length - 1] = hi;
      for (let i = up.length - 2; i >= 0; i--) up[i] = Math.min(up[i]!, up[i + 1]! - space);
      if (up.every((y, i) => y >= lo && clear(y, blocked[i]!))) ys = up;
      else {
        overflow = Math.max(overflow, ys[ys.length - 1]! - hi);
        if (final) ys = spread(ys, space, lo, hi);
      }
    }
    col.forEach((n, i) => (n.labelY = ys[i]!));
  }

  return { layout: { width, height, nodeWidth, fontSize, nodes: list, links }, overflow, shifts };
}

/** One sentence for screen readers with every count the chart shows. */
export function describeFunnel(f: Funnel): string {
  const v = sankeyValues(f);
  const parts = [`${v.swiped} swiped right`, `${v.sent} sent`];
  if (v.waiting) parts.push(`${v.waiting} sending`);
  if (v.failed) parts.push(`${v.failed} didn't send`);
  let text = `Job search: ${parts.join(", ")}.`;
  if (v.sent) text += ` Of those sent, ${v.interviews} got an interview, ${v.rejected} rejected, ${v.noReply} no reply.`;
  if (v.interviews) text += ` ${v.offers} ${v.offers === 1 ? "offer" : "offers"}, ${v.noOffer} no offer, ${v.interviewing} still interviewing.`;
  return text;
}

const css = (token: string) => `var(${token})`;

/** How strongly a ribbon takes the colour of the stage it flows into: bold stages tint the band, quiet ones barely. It fades to 60% at its source. */
export const ribbon = (alpha: number) => Math.max(0.2, alpha * 0.36);

export type SankeyProps = { funnel: Funnel; className?: string };

/** The whole job search as a Sankey chart: swiped right, through sent, to interviews and offers. Draws in on mount. */
export function Sankey({ funnel, className }: SankeyProps) {
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [, setFonts] = useState(0);
  const still = useReducedMotion();
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");

  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const ro = new ResizeObserver(([e]) => setWidth(Math.round(e!.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    let live = true;
    document.fonts?.ready.then(() => live && setFonts((n) => n + 1));
    return () => {
      live = false;
    };
  }, []);

  const compact = width > 0 && width < 520;
  const height = compact ? 236 : 300;
  const layout = useMemo(
    () => (width ? layoutSankey(funnel, { width, height, fontSize: compact ? 11 : 13, nodeWidth: compact ? 6 : 8, gap: compact ? 8 : 12 }) : null),
    [funnel, width, height, compact],
  );

  return (
    <div ref={box} className={cn("w-full", className)} style={{ height }}>
      {layout && (
        <svg width={layout.width} height={layout.height} viewBox={`0 0 ${layout.width} ${layout.height}`} role="img" aria-label={describeFunnel(funnel)} className="block overflow-visible">
          <defs>
            <clipPath id={`reveal-${uid}`}>
              <motion.rect
                x={0}
                y={-20}
                height={layout.height + 40}
                initial={still ? false : { width: 0 }}
                animate={{ width: layout.width + 20 }}
                transition={{ duration: 1.1, ease }}
              />
            </clipPath>
            {layout.links.map((l, i) => (
              <linearGradient key={l.to.key} id={`band-${uid}-${i}`} gradientUnits="userSpaceOnUse" x1={l.x0} x2={l.x1} y1={0} y2={0}>
                <stop offset="0%" style={{ stopColor: css(l.to.color), stopOpacity: ribbon(l.to.alpha) * 0.6 }} />
                <stop offset="100%" style={{ stopColor: css(l.to.color), stopOpacity: ribbon(l.to.alpha) }} />
              </linearGradient>
            ))}
          </defs>
          <g clipPath={`url(#reveal-${uid})`}>
            {layout.links.map((l, i) => (
              <path key={l.to.key} d={l.d} style={{ fill: `url(#band-${uid}-${i})` }} />
            ))}
            {layout.nodes.map((n) => (
              <rect key={n.key} x={n.x} y={n.y} width={layout.nodeWidth} height={n.h} style={{ fill: css(n.color), fillOpacity: n.alpha === 1 ? 1 : Math.min(1, n.alpha + 0.25) }} />
            ))}
          </g>
          {layout.nodes.map((n) => (
            <motion.text
              key={n.key}
              x={n.labelX}
              y={n.labelY}
              dominantBaseline="central"
              initial={still ? false : { opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, ease, delay: still ? 0 : 0.25 + n.col * 0.22 }}
              className="pointer-events-none tabular-nums"
              style={{ fontSize: layout.fontSize, ...(n.above ? {} : { paintOrder: "stroke", stroke: "var(--surface)", strokeWidth: 3, strokeLinejoin: "round" }) }}
            >
              <tspan className="fill-ink-soft font-semibold">{n.label} </tspan>
              <tspan className="fill-ink font-extrabold">{n.value}</tspan>
            </motion.text>
          ))}
        </svg>
      )}
    </div>
  );
}
